import { get, post } from '@api/request';

export async function fetchAdministrationNightlyTotals(
  { appraisalNoticeService },
  authenticationToken,
) {
  const url = `${appraisalNoticeService}/trueprodigy/administration/nightlytotals`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function queryExecutorAdministration(
  { appraisalNoticeService },
  authenticationToken,
  payload,
) {
  const url = `${appraisalNoticeService}/trueprodigy/administration/execute`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function fetchPropertyLetterFields(
  { hsAuditService },
  authenticationToken,
) {
  const url = `${hsAuditService}/hsaudit/letterfields/property`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function postLetter(
  { hsAuditService },
  authenticationToken,
  payload,
) {
  const url = `${hsAuditService}/hsaudit/letters`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: authenticationToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const body = await response.json();
  return body;
}

export async function deleteLetter(
  { hsAuditService },
  authenticationToken,
  letterID,
) {
  const url = `${hsAuditService}/hsaudit/letter/${letterID}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: authenticationToken,
    },
  });
  const body = await response.json();
  return body;
}

/* Fetch letters. Accepts countOnly parameter to facilitate pagination */
export async function fetchLetters({
  hsAuditService,
  authenticationToken,
  countOnly = false,
  page = 1,
  pageSize = 50,
  sortDirection = 'DESC',
  sortField = 'letterID',
}) {
  const url = countOnly
    ? `${hsAuditService}/hsaudit/letters?countOnly=1`
    : `${hsAuditService}/hsaudit/letters?page=${page}&pageSize=${pageSize}&sortField=${sortField}&sortDirection=${sortDirection}`;
  const response = await fetch(url, {
    headers: {
      Authorization: authenticationToken,
    },
  });
  const body = await response.json();
  return body;
}

export async function generateLetters({
  hsAuditService,
  authenticationToken,
  payload,
}) {
  const url = `${hsAuditService}/hsaudit/letters`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: authenticationToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const body = await response.json();
  return body;
}

export async function saveHtml({
  hsAuditService,
  authenticationToken,
  letterID,
  payload,
}) {
  const url = `${hsAuditService}/hsaudit/letter/${letterID}/html`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: authenticationToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const body = await response.json();
  return body;
}

export async function fetchHtml({
  hsAuditService,
  authenticationToken,
  letterID,
}) {
  const url = `${hsAuditService}/hsaudit/letter/${letterID}/html`;
  const response = await fetch(url, {
    headers: {
      Authorization: authenticationToken,
    },
  });
  const body = await response.json();
  return body;
}

export async function fetchLetter({
  hsAuditService,
  authenticationToken,
  letterID,
}) {
  const url = `${hsAuditService}/hsaudit/letter/${letterID}`;
  const response = await fetch(url, {
    headers: {
      Authorization: authenticationToken,
    },
  });
  const body = await response.json();
  return body;
}

export async function fetchLetterJobs({
  hsAuditService,
  authenticationToken,
  page = 1,
  pageSize = 50,
  sortField = 'runID',
  sortDirection = 'DESC',
}) {
  const url = `${hsAuditService}/hsaudit/letter/jobs?page=${page}&pageSize=${pageSize}&sortField=${sortField}&sortDirection=${sortDirection}`;

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: authenticationToken,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const body = await response.json();
    return body;
  } catch (error) {
    console.error('Error fetching letter jobs:', error);
    throw error;
  }
}

// export async function getRenderedHtml({
//   hsAuditService,
//   authenticationToken,
//   payload,
//   pID,
//   pYear,
// }) {
//   const url = `${hsAuditService}/hsaudit/render/${pID}/${pYear}`;
//   const response = await fetch(url, {
//     method: 'POST',
//     headers: {
//       Authorization: authenticationToken,
//       'Content-Type': 'application/json',
//     },
//     body: JSON.stringify(payload),
//   });
//   return response.json();
// }

export async function getRenderedHtml({
  hsAuditService,
  authenticationToken,
  payload,
  pID,
  pYear,
}) {
  const url = `${hsAuditService}/hsaudit/render/${pID}/${pYear}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: authenticationToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.blob();
}

export async function generateLetterPDF(
  { hsAuditService },
  authenticationToken,
  { payload, pID, pYear },
) {
  const url = `${hsAuditService}/hsaudit/lettergeneratepdf/${pID}/${pYear}`;

  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const headers = new Headers();
  headers.append('Authorization', authenticationToken);
  headers.append('Content-Type', 'application/json');

  return await fetch(url, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(payload),
  })
    .then((response) => response.blob())
    .then((blobby) => {
      const objectUrl = window.URL.createObjectURL(blobby);

      anchor.href = objectUrl;
      anchor.download = `${pID}-${pYear}-letter.pdf`;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
}

export async function createLetterJob({
  hsAuditService,
  authenticationToken,
  letterID,
  pIDs,
  pYear,
  query,
  createEvent,
}) {
  const url = `${hsAuditService}/hsaudit/letter/${letterID}/job`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: authenticationToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      letterID,
      pIDs,
      pYear,
      createEvent,
      query,
    }),
  });
  return response.json();
}

export async function createSingleLetter({
  authenticationToken,
  createEvent,
  hsAuditService,
  letterID,
  ownerID,
  pAccountID,
  pID,
  pYear,
}) {
  const url = `${hsAuditService}/hsaudit/letter/${letterID}/single`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: authenticationToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      createEvent,
      letterID,
      ownerID,
      pAccountID,
      pID,
      pYear,
    }),
  });
  return response.blob();
}

export async function validateLetterQuery({
  hsAuditService,
  authenticationToken,
  query,
  letterType,
}) {
  const url = `${hsAuditService}/hsaudit/validate`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: authenticationToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query,
      letterType,
    }),
  });
  return response.json();
}

export async function fetchLetterJobDetails({
  hsAuditService,
  authenticationToken,
  jobID,
  page = 1,
  pageSize = 50,
  status = 'all', // 'all', 'success', or 'error'
}) {
  const url = `${hsAuditService}/hsaudit/letter/job/${jobID}/details?page=${page}&pageSize=${pageSize}${
    status !== 'all' ? `&status=${status}` : ''
  }`;

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: authenticationToken,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const body = await response.json();
    return body;
  } catch (error) {
    console.error('Error fetching letter job details:', error);
    throw error;
  }
}

export async function fetchLetterJobsCount({
  hsAuditService,
  authenticationToken,
}) {
  const url = `${hsAuditService}/hsaudit/letter/jobs?countOnly=1`;
  const response = await fetch(url, {
    headers: {
      Authorization: authenticationToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function fetchLetterJobDetailsCount({
  hsAuditService,
  authenticationToken,
  jobID,
  status = 'all',
}) {
  const url = `${hsAuditService}/hsaudit/letter/job/${jobID}/details?countOnly=1${
    status !== 'all' ? `&status=${status}` : ''
  }`;
  const response = await fetch(url, {
    headers: {
      Authorization: authenticationToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function downloadLetterJobFile({
  hsAuditService,
  authenticationToken,
  jobID,
  s3ID,
}) {
  const url = `${hsAuditService}/hsaudit/letter/job/${jobID}/file/${s3ID}`;

  const response = await fetch(url, {
    headers: {
      Authorization: authenticationToken,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const blob = await response.blob();
  const anchor = document.createElement('a');
  document.body.appendChild(anchor);
  const objectUrl = window.URL.createObjectURL(blob);

  anchor.href = objectUrl;
  anchor.download = 'File.zip';
  anchor.click();

  window.URL.revokeObjectURL(objectUrl);
  document.body.removeChild(anchor);
}
